import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { axiosClient } from '../../../core/axios-client'

export const fetchPaymentLinks = createAsyncThunk(
  'paymentLinks/fetchPaymentLinks',
  async ({ companyId } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      const response = await axiosClient.get(`/companies/${companyId}/payment-links`)
      return { data: response.data, companyId: String(companyId) }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const createPaymentLink = createAsyncThunk(
  'paymentLinks/createPaymentLink',
  async ({ companyId, payload } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      const response = await axiosClient.post(`/companies/${companyId}/payment-links`, payload)
      return { data: response.data, companyId: String(companyId) }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const updatePaymentLink = createAsyncThunk(
  'paymentLinks/updatePaymentLink',
  async ({ companyId, linkId, payload } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      if (!linkId) throw new Error('linkId is required')
      const response = await axiosClient.patch(
        `/companies/${companyId}/payment-links/${linkId}`,
        payload
      )
      return { data: response.data, companyId: String(companyId), linkId: String(linkId) }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const deletePaymentLink = createAsyncThunk(
  'paymentLinks/deletePaymentLink',
  async ({ companyId, linkId } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      if (!linkId) throw new Error('linkId is required')
      await axiosClient.delete(`/companies/${companyId}/payment-links/${linkId}`)
      return { companyId: String(companyId), linkId: String(linkId) }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const initialState = {
  items: [],
  status: 'idle',
  error: null,
  listCompanyId: null,

  saveStatus: 'idle',
  saveError: null,
}

export const slice = createSlice({
  name: 'paymentLinks',
  initialState,

  reducers: {
    resetPaymentLinksState(state) {
      state.items = []
      state.status = 'idle'
      state.error = null
      state.listCompanyId = null
    },
    resetPaymentLinksSaveState(state) {
      state.saveStatus = 'idle'
      state.saveError = null
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentLinks.pending, (state, action) => {
        state.status = 'loading'
        state.error = null
        state.listCompanyId = String(action.meta.arg?.companyId ?? '')
      })
      .addCase(fetchPaymentLinks.fulfilled, (state, { payload }) => {
        state.status = 'succeeded'
        state.items = Array.isArray(payload?.data) ? payload.data : []
        state.listCompanyId = payload?.companyId ?? state.listCompanyId
      })
      .addCase(fetchPaymentLinks.rejected, (state, { payload }) => {
        state.status = 'failed'
        state.error = payload || 'Failed to fetch payment links'
      })

      .addCase(createPaymentLink.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(createPaymentLink.fulfilled, (state, { payload }) => {
        state.saveStatus = 'succeeded'
        if (payload?.data) state.items = [...state.items, payload.data]
      })
      .addCase(createPaymentLink.rejected, (state, { payload }) => {
        state.saveStatus = 'failed'
        state.saveError = payload || 'Failed to create payment link'
      })

      .addCase(updatePaymentLink.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(updatePaymentLink.fulfilled, (state, { payload }) => {
        state.saveStatus = 'succeeded'
        const linkId = String(payload?.linkId ?? '')
        const idx = state.items.findIndex((x) => String(x?.id) === linkId)
        if (idx >= 0 && payload?.data) state.items[idx] = payload.data
      })
      .addCase(updatePaymentLink.rejected, (state, { payload }) => {
        state.saveStatus = 'failed'
        state.saveError = payload || 'Failed to update payment link'
      })

      .addCase(deletePaymentLink.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(deletePaymentLink.fulfilled, (state, { payload }) => {
        state.saveStatus = 'succeeded'
        const linkId = String(payload?.linkId ?? '')
        state.items = state.items.filter((x) => String(x?.id) !== linkId)
      })
      .addCase(deletePaymentLink.rejected, (state, { payload }) => {
        state.saveStatus = 'failed'
        state.saveError = payload || 'Failed to delete payment link'
      })
  },
})

export const { resetPaymentLinksState, resetPaymentLinksSaveState } = slice.actions

export default slice.reducer
